import { useDispatch, useSelector } from 'react-redux';
import { setAuthorisedModule, setLoadCount } from '@stores/Modules/slice';
import { useLazyGetModulesQuery, useLazyGetRequestedSubModulesQuery } from '../../api/userModules';
import { Module } from '@stores/Modules/types/modules';
import { convertRequestedSubModulesToModules, updateSubmoduleOrdering } from '@utils/common';
import { RootState } from '@stores/store';
import {
  DEMO_USER_EMAILS,
  GETTING_STARTED_DEMO_MODULES,
  GETTING_STARTED_MODULES,
} from '@constants/constants';
import UserAuth from '@services/auth';

const EXCLUDED_SUBMODULE_TYPES = ['FUNDING_REQUEST', 'GETTING_STARTED'];

const useUserModules = () => {
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state?.UserData);
  const [getModules, { isLoading, isError }] = useLazyGetModulesQuery();
  const [getRequestedSubModules, { isLoading: isRequestedSubModulesLoading }] =
    useLazyGetRequestedSubModulesQuery();

  const getUserModules = async (isAdmin?: boolean): Promise<void> => {
    try {
      const response = await getModules().unwrap();
      const requestedSubModules = await getRequestedSubModules().unwrap();

      let authorizedModules: Module[] = response.data.modules.map((module) => ({
        id: module.id,
        name: module.name,
        requestStatus: 'APPROVED',
        submodules:
          module.submoduleCatalogs?.map((submodule) => ({
            id: submodule.id,
            name: submodule.name,
            url: submodule.url,
            embedId: submodule.embedId || '',
            submoduleType: submodule.submoduleType,
            defaultFilter: submodule.defaultFilter || '',
            nativeFilterId: submodule.nativeFilterId || '',
          })) || [],
        metadata: module.metadata,
      }));

      updateSubmoduleOrdering(authorizedModules);
      authorizedModules = [
        ...authorizedModules,
        ...convertRequestedSubModulesToModules(requestedSubModules.data),
      ];

      let updatedAuthorizedModules =
        userDetails?.user?.role === 'ADMIN'
          ? authorizedModules.filter(
              (option) =>
                !EXCLUDED_SUBMODULE_TYPES.includes(option?.submodules?.[0]?.submoduleType || ''),
            )
          : authorizedModules;

      const isDemoUser =
        userDetails.user.email &&
        DEMO_USER_EMAILS.includes(userDetails.user.email) &&
        !UserAuth.demoToken;
      const isPendingAccessUser =
        response.data.modules?.length === 1 &&
        response.data.modules.some((module) =>
          module.submoduleCatalogs?.some(
            (submodule) => submodule.submoduleType === 'GETTING_STARTED',
          ),
        );
      if (isDemoUser) {
        updatedAuthorizedModules = [...GETTING_STARTED_DEMO_MODULES];
      } else if (isPendingAccessUser) {
        updatedAuthorizedModules = [
          ...updatedAuthorizedModules,
          ...GETTING_STARTED_MODULES.filter((module) =>
            module?.submodules?.some(
              (submodule) => submodule.submoduleType !== 'FINANCIAL_PROJECTIONS',
            ),
          ),
        ];
      }

      dispatch(
        setAuthorisedModule({
          modules: updatedAuthorizedModules,
          userManagementModules: authorizedModules,
        }),
      );
    } catch (error) {
      console.error('Error fetching user modules:', error);
    } finally {
      dispatch(setLoadCount());
    }
  };

  return {
    getUserModules,
    isLoading: isLoading || isRequestedSubModulesLoading,
    isError,
  };
};

export default useUserModules;
